import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Crown, Shield, Mail, Phone, MapPin, Briefcase, GraduationCap, Calendar, Heart, MessageSquare, Trash2, CheckCircle, XCircle, Lock, Unlock } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // No longer using Profile Approval page
  const profileData = location.state?.profileData;

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data.user);
      setStats(response.data.stats);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load user details",
        variant: "destructive",
      });
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const togglePremium = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/api/admin/users/${userId}/premium`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast({
        title: "Success",
        description: response.data.message,
      });
      fetchUserDetails();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update premium status",
        variant: "destructive",
      });
    }
  };

  const toggleAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/api/admin/users/${userId}/admin`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast({
        title: "Success",
        description: response.data.message,
      });
      fetchUserDetails();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update admin status",
        variant: "destructive",
      });
    }
  };

  const toggleVerification = async () => {
    const isCurrentlyVerified = user.govIdVerified || user.isVerified;
    const action = isCurrentlyVerified ? 'remove verification from' : 'verify';
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/api/admin/users/${userId}/verify`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast({
        title: "Success",
        description: response.data.message,
      });
      fetchUserDetails();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update verification status",
        variant: "destructive",
      });
    }
  };

  const deleteUser = async () => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
      navigate('/admin/users');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive",
      });
    }
  };

  const toggleProfileLock = async () => {
    const isCurrentlyLocked = user.isNameLocked;
    const action = isCurrentlyLocked ? 'unlock' : 'lock';
    
    if (!window.confirm(`Are you sure you want to ${action} this user's profile fields (name & picture)?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/${action}-profile/${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast({
        title: "Success",
        description: `Profile fields ${action}ed successfully`,
      });
      fetchUserDetails();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || `Failed to ${action} profile fields`,
        variant: "destructive",
      });
    }
  };


  // Removed old profile approval functions - now using Identity Verifications only

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  if (!user) {
    return <div>User not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">User Details</h1>
            <p className="text-gray-600 mt-1">Complete profile information</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <>
              {/* User Management Actions */}
              <button
                onClick={togglePremium}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                  user.isPremium
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Crown className="w-4 h-4" />
                {user.isPremium ? 'Remove Premium' : 'Make Premium'}
              </button>
              <button
                onClick={toggleAdmin}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                  user.isAdmin
                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Shield className="w-4 h-4" />
                {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
              </button>
              <button
                onClick={toggleVerification}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                  (user.govIdVerified || user.isVerified)
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                {(user.govIdVerified || user.isVerified) ? 'Remove Verified' : 'Verify User'}
              </button>
              {(user.govIdVerified || user.isVerified) && (
                <button
                  onClick={toggleProfileLock}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    user.isNameLocked
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {user.isNameLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {user.isNameLocked ? 'Unlock Profile Fields' : 'Lock Profile Fields'}
                </button>
              )}
              <button
                onClick={deleteUser}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete User
              </button>
            </>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start gap-6">
              <img
                src={user.image || 'https://via.placeholder.com/150'}
                alt={user.firstName}
                className="w-32 h-32 rounded-lg object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {user.firstName} {user.lastName}
                  </h2>
                  {user.isPremium && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <Crown className="w-4 h-4 mr-1" />
                      Premium
                    </span>
                  )}
                  {user.isAdmin && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      <Shield className="w-4 h-4 mr-1" />
                      Admin
                    </span>
                  )}
                  {(user.govIdVerified || user.isVerified) && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Verified
                    </span>
                  )}
                </div>
                <p className="text-gray-600 mb-4">{user.about || 'No bio available'}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{user.age} years old</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="font-medium capitalize">{user.gender}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Contact Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{user.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{user.phone}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{user.location || 'Not specified'}</span>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Professional Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Education</span>
                </div>
                <p className="text-gray-800 font-medium">{user.education || 'Not specified'}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Occupation</span>
                </div>
                <p className="text-gray-800 font-medium">{user.occupation || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600 block mb-1">Income</span>
                <p className="text-gray-800 font-medium">{user.income || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600 block mb-1">Marital Status</span>
                <p className="text-gray-800 font-medium">{user.maritalStatus || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Religious Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Religious Background</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-600 block mb-1">Religion</span>
                <p className="text-gray-800 font-medium">{user.religion || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600 block mb-1">Caste</span>
                <p className="text-gray-800 font-medium">{user.caste || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600 block mb-1">Language</span>
                <p className="text-gray-800 font-medium">{user.motherTongue || 'Not specified'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Stats & Actions */}
        <div className="space-y-6">
          {/* Activity Stats */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Activity Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  <span className="text-gray-700">Interests Sent</span>
                </div>
                <span className="text-xl font-bold text-pink-600">{stats?.sentInterests || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-cream-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-navy-500" />
                  <span className="text-gray-700">Interests Received</span>
                </div>
                <span className="text-xl font-bold text-navy-500">{stats?.receivedInterests || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Messages Sent</span>
                </div>
                <span className="text-xl font-bold text-blue-600">{stats?.sentMessages || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />
                  <span className="text-gray-700">Messages Received</span>
                </div>
                <span className="text-xl font-bold text-indigo-600">{stats?.receivedMessages || 0}</span>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Account Info</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-gray-600">Profile For:</span>
                <p className="text-gray-800 font-medium capitalize">{user.profileFor || 'Self'}</p>
              </div>
              <div>
                <span className="text-gray-600">Date of Birth:</span>
                <p className="text-gray-800 font-medium">{user.dob || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-gray-600">Height:</span>
                <p className="text-gray-800 font-medium">{user.height || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-gray-600">Created:</span>
                <p className="text-gray-800 font-medium">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-gray-600">Last Active:</span>
                <p className="text-gray-800 font-medium">
                  {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'Never'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
    </div>
  );
};

export default AdminUserDetail;
